import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, CheckCircle2, XCircle, ClipboardCheck, AlertTriangle, Printer,
} from "lucide-react";

const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const nowHm = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const SetupChecksheet = () => {
  const { jobId, checksheetId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [template, setTemplate] = useState(null);
  const [job, setJob] = useState(null);
  const [existing, setExisting] = useState(null);
  const [values, setValues] = useState({}); // key -> {status, remark}
  const [meta, setMeta] = useState({
    machine_code: params.get("machine") || "",
    tool_no: params.get("tool") || "",
    part_name: "",
    part_no: "",
    operator_name: "",
    operator_id: "",
    checksheet_date: todayIso(),
    checksheet_time: nowHm(),
    final_remark: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const readOnly = Boolean(checksheetId);

  useEffect(() => {
    api.get("/setup-checksheet/template").then((r) => setTemplate(r.data));
  }, []);

  useEffect(() => {
    if (jobId) {
      api.get(`/production/job/${jobId}`).then((r) => {
        const j = r.data?.job;
        if (j) {
          setJob(j);
          setMeta((m) => ({
            ...m,
            machine_code: j.machine_code || m.machine_code,
            tool_no: j.tool_no || m.tool_no,
            part_name: j.part_name || "",
            part_no: j.part_no || "",
            operator_name: j.operator_name || "",
            operator_id: j.operator_id || "",
          }));
        }
      }).catch(() => {});
    }
  }, [jobId]);

  useEffect(() => {
    if (checksheetId) {
      api.get(`/setup-checksheet/${checksheetId}`).then((r) => {
        setExisting(r.data);
        setMeta((m) => ({ ...m, ...r.data }));
        const v = {};
        (r.data.results || []).forEach((row) => {
          v[`${row.section_key}::${row.idx}`] = { status: row.status, remark: row.remark || "" };
        });
        setValues(v);
      }).catch((e) => setError(e.response?.data?.detail || "Failed to load"));
    }
  }, [checksheetId]);

  const setResult = (key, patch) => {
    setValues((prev) => ({ ...prev, [key]: { ...(prev[key] || { status: "", remark: "" }), ...patch } }));
  };

  const buildResults = () => {
    const out = [];
    (template?.sections || []).forEach((sec) => {
      (sec.checkpoints || []).forEach((cp, idx) => {
        const key = `${sec.key}::${idx}`;
        const v = values[key] || { status: "", remark: "" };
        out.push({ section_key: sec.key, idx, label: cp, status: v.status || "NA", remark: v.remark || "" });
      });
    });
    return out;
  };

  const validate = () => {
    const results = buildResults();
    const missing = results.find((r) => !["OK", "Not OK", "NA"].includes(r.status) || r.status === "");
    if (missing) {
      setError("Please mark OK / Not OK for every checkpoint");
      return false;
    }
    const notOkNoRemark = results.find((r) => r.status === "Not OK" && !r.remark.trim());
    if (notOkNoRemark) {
      setError(`"${notOkNoRemark.label}" is marked Not OK — please add a remark.`);
      return false;
    }
    if (!meta.machine_code || !meta.tool_no) {
      setError("Machine and Tool No. are required.");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        job_id: jobId || null,
        machine_code: meta.machine_code,
        tool_no: meta.tool_no,
        part_name: meta.part_name,
        part_no: meta.part_no,
        operator_name: meta.operator_name,
        operator_id: meta.operator_id,
        checksheet_date: meta.checksheet_date,
        checksheet_time: meta.checksheet_time,
        results: buildResults(),
        final_remark: meta.final_remark,
      };
      const res = await api.post("/setup-checksheet", payload);
      setToast(`Checksheet ${res.data.checksheet_id} saved — status ${res.data.overall_status}`);
      setTimeout(() => navigate(-1), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-4xl mx-auto p-6 print:p-2">
        <div className="flex items-center justify-between mb-3 print:hidden">
          <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <ArrowLeft size={14} /> Back
          </button>
          <button onClick={() => window.print()} className="text-xs font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <Printer size={12} /> Print
          </button>
        </div>

        <div className="bg-gradient-to-r from-[#1A3560] to-[#2d4a7d] text-white rounded-2xl p-5 shadow-lg mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Tool Setup Approval Checksheet</p>
              <h1 className="text-2xl font-extrabold inline-flex items-center gap-2">
                <ClipboardCheck size={22} /> {readOnly ? existing?.checksheet_id : "New Setup"}
              </h1>
              <p className="text-xs opacity-80">{template?.version}</p>
            </div>
            {readOnly && existing && (
              <span className={`px-3 py-1 text-xs font-extrabold rounded-full ${existing.overall_status === "OK" ? "bg-emerald-500" : "bg-red-500"}`}>
                {existing.overall_status}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 mb-4 inline-flex items-center gap-2">
            <AlertTriangle size={14} /> {error}
          </div>
        )}
        {toast && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800 mb-4 inline-flex items-center gap-2">
            <CheckCircle2 size={14} /> {toast}
          </div>
        )}

        {/* Header block */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetaField label="Machine No." value={meta.machine_code} onChange={(v) => setMeta({ ...meta, machine_code: v })} readOnly={readOnly} testid="scs-machine" />
            <MetaField label="Tool No." value={meta.tool_no} onChange={(v) => setMeta({ ...meta, tool_no: v })} readOnly={readOnly} testid="scs-tool" />
            <MetaField label="Part No." value={meta.part_no} onChange={(v) => setMeta({ ...meta, part_no: v })} readOnly={readOnly} />
            <MetaField label="Part Name" value={meta.part_name} onChange={(v) => setMeta({ ...meta, part_name: v })} readOnly={readOnly} />
            <MetaField label="Operator Name" value={meta.operator_name} onChange={(v) => setMeta({ ...meta, operator_name: v })} readOnly={readOnly} />
            <MetaField label="Operator ID" value={meta.operator_id} onChange={(v) => setMeta({ ...meta, operator_id: v })} readOnly={readOnly} />
            <MetaField label="Date" type="date" value={meta.checksheet_date} onChange={(v) => setMeta({ ...meta, checksheet_date: v })} readOnly={readOnly} />
            <MetaField label="Time" type="time" value={meta.checksheet_time} onChange={(v) => setMeta({ ...meta, checksheet_time: v })} readOnly={readOnly} />
          </div>
        </div>

        {/* Sections */}
        {(template?.sections || []).map((sec) => (
          <div key={sec.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-4 overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <p className="text-sm font-extrabold text-slate-700">{sec.title}</p>
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-3 py-2 w-10 text-left">#</th>
                  <th className="px-3 py-2 text-left">Checkpoint</th>
                  <th className="px-3 py-2 w-20 text-center">OK</th>
                  <th className="px-3 py-2 w-24 text-center">Not OK</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {sec.checkpoints.map((cp, idx) => {
                  const key = `${sec.key}::${idx}`;
                  const v = values[key] || { status: "", remark: "" };
                  return (
                    <tr key={key} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-xs text-slate-400">{idx + 1}</td>
                      <td className="px-3 py-2 text-slate-700">{cp}</td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="radio"
                          name={key}
                          data-testid={`scs-${sec.key}-${idx}-ok`}
                          disabled={readOnly}
                          checked={v.status === "OK"}
                          onChange={() => setResult(key, { status: "OK" })}
                          className="accent-emerald-600"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="radio"
                          name={key}
                          data-testid={`scs-${sec.key}-${idx}-notok`}
                          disabled={readOnly}
                          checked={v.status === "Not OK"}
                          onChange={() => setResult(key, { status: "Not OK" })}
                          className="accent-red-600"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          disabled={readOnly}
                          value={v.remark}
                          placeholder={v.status === "Not OK" ? "Required" : "Optional remark"}
                          onChange={(e) => setResult(key, { remark: e.target.value })}
                          className={`w-full text-xs rounded-lg border px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#1A3560] ${v.status === "Not OK" && !v.remark.trim() ? "border-red-300 bg-red-50" : "border-slate-200"}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Final Remark</label>
          <textarea
            data-testid="scs-final-remark"
            disabled={readOnly}
            value={meta.final_remark}
            onChange={(e) => setMeta({ ...meta, final_remark: e.target.value })}
            rows={2}
            className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
            placeholder="Optional notes on approval or deviations…"
          />
        </div>

        {!readOnly && (
          <div className="flex justify-end print:hidden">
            <button
              data-testid="scs-submit"
              onClick={handleSubmit}
              disabled={submitting}
              className="text-sm font-extrabold text-white bg-[#15803D] hover:bg-emerald-700 disabled:bg-slate-300 px-5 py-2.5 rounded-lg transition-all inline-flex items-center gap-1.5 shadow"
            >
              <ClipboardCheck size={14} /> {submitting ? "Saving…" : "Submit Checksheet"}
            </button>
          </div>
        )}
        {readOnly && existing && (
          <p className="text-xs text-slate-400 text-right">Submitted by {existing.submitted_by_name || existing.submitted_by} on {existing.submitted_at}</p>
        )}
      </div>
    </div>
  );
};

const MetaField = ({ label, value, onChange, readOnly, type = "text", testid }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
    <input
      type={type}
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={readOnly}
      className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
    />
  </label>
);

export default SetupChecksheet;
